import React, { useState, useEffect } from 'react';

interface Cooldown {
  id: string;
  name: string;
  expiresAt: number;
  totalDuration: number;
  color: string;
  type: 'wildcard' | 'consumable' | 'ability' | 'other';
}

interface CombatCooldownTrackerProps {
  cooldowns: Cooldown[];
  isInCombat: boolean;
}

const injectStyles = (): void => {
  const styleId = 'combat-cooldown-tracker-style';
  if (document.getElementById(styleId)) return;
  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    .combat-cooldown-tracker {
      position: fixed;
      left: 20px;
      bottom: 20px;
      font-family: 'Sometype Mono', monospace;
      z-index: 1000;
    }

    .combat-cooldown-container {
      background: rgba(10, 10, 26, 0.95);
      border: 1px solid #2a2a4e;
      padding: 12px;
      min-width: 200px;
      max-width: 240px;
    }

    .combat-cooldown-header {
      font-size: 9px;
      color: #666;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 8px;
      padding-bottom: 6px;
      border-bottom: 1px solid #2a2a4e;
    }

    .combat-cooldown-list {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .combat-cooldown-item {
      display: flex;
      flex-direction: column;
      gap: 3px;
    }

    .combat-cooldown-name {
      font-size: 11px;
      font-weight: bold;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .combat-cooldown-type-badge {
      font-size: 8px;
      padding: 1px 4px;
      border-radius: 2px;
      text-transform: uppercase;
      letter-spacing: 0.3px;
      opacity: 0.7;
    }

    .combat-cooldown-type-badge.signature {
      background: rgba(79, 195, 247, 0.2);
      color: #4fc3f7;
    }

    .combat-cooldown-type-badge.consumable {
      background: rgba(102, 187, 106, 0.2);
      color: #66bb6a;
    }

    .combat-cooldown-type-badge.ability {
      background: rgba(255, 167, 38, 0.2);
      color: #ffa726;
    }

    .combat-cooldown-type-badge.other {
      background: rgba(156, 39, 176, 0.2);
      color: #ab47bc;
    }

    .combat-cooldown-bar {
      height: 6px;
      background: #1a1a2e;
      border: 1px solid #2a2a4e;
      overflow: hidden;
    }

    .combat-cooldown-fill {
      height: 100%;
      transition: width 0.1s linear;
    }

    .combat-cooldown-timer {
      font-size: 9px;
      color: #888;
      text-align: right;
    }

    .combat-cooldown-empty {
      font-size: 10px;
      color: #666;
      text-align: center;
      padding: 8px 0;
      font-style: italic;
    }
  `;
  document.head.appendChild(style);
};

export const CombatCooldownTracker: React.FC<CombatCooldownTrackerProps> = ({
  cooldowns,
  isInCombat,
}) => {
  const [displayCooldowns, setDisplayCooldowns] = useState<Array<Cooldown & { remainingMs: number; percent: number; timeStr: string }>>([]);

  useEffect(() => {
    injectStyles();
  }, []);

  useEffect(() => {
    const updateCooldowns = (): void => {
      const now = Date.now();
      const updated = cooldowns
        .map(cd => {
          const remainingMs = Math.max(0, cd.expiresAt - now);
          const percent = (remainingMs / cd.totalDuration) * 100;
          const seconds = Math.ceil(remainingMs / 1000);
          const timeStr = seconds > 0 ? `${seconds}s` : 'Ready';
          return { ...cd, remainingMs, percent, timeStr };
        })
        .filter(cd => cd.remainingMs > 0)
        .sort((a, b) => a.remainingMs - b.remainingMs);
      
      setDisplayCooldowns(updated);
    };

    updateCooldowns();
    const interval = setInterval(updateCooldowns, 100);
    return () => clearInterval(interval);
  }, [cooldowns]);

  if (!isInCombat || displayCooldowns.length === 0) {
    return null;
  }

  return (
    <div className="combat-cooldown-tracker">
      <div className="combat-cooldown-container">
        <div className="combat-cooldown-header">Active Cooldowns</div>
        <div className="combat-cooldown-list">
          {displayCooldowns.map(cd => (
            <div key={cd.id} className="combat-cooldown-item">
              <div className="combat-cooldown-name" style={{ color: cd.color }}>
                <span>{cd.name}</span>
                <span className={`combat-cooldown-type-badge ${cd.type}`}>
                  {cd.type}
                </span>
              </div>
              <div className="combat-cooldown-bar">
                <div
                  className="combat-cooldown-fill"
                  style={{
                    width: `${cd.percent}%`,
                    background: cd.color,
                    opacity: 0.7,
                  }}
                />
              </div>
              <div className="combat-cooldown-timer">{cd.timeStr}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
